/**
 * The judge prompt. A textarea over a mirror of the same text, so each
 * `{{variable}}` shows highlighted while it stays a plain textarea to type in.
 * Both layers share font, padding and wrapping; the mirror follows the scroll.
 */

import { useRef } from "react";
import { promptPieces } from "./variables";

const LAYER =
  "col-start-1 row-start-1 m-0 min-h-56 w-full px-3 py-2.5 font-mono text-xs leading-relaxed whitespace-pre-wrap break-words";

export function PromptEditor({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const mirror = useRef<HTMLDivElement>(null);
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-semibold">
        Prompt
      </label>
      <div className="grid overflow-hidden rounded-[7px] border border-input bg-surface focus-within:border-focus">
        <div ref={mirror} aria-hidden className={`${LAYER} pointer-events-none overflow-hidden text-ink`}>
          {promptPieces(value).map((p, i) =>
            p.variable ? (
              <mark key={i} className="rounded-[3px] bg-run-bg text-link">
                {p.text}
              </mark>
            ) : (
              <span key={i}>{p.text}</span>
            ),
          )}
          {/* A trailing newline needs a character after it to take up a line. */}
          {"​"}
        </div>
        <textarea
          id={id}
          className={`${LAYER} resize-y bg-transparent text-transparent caret-ink outline-none`}
          spellCheck={false}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onScroll={(e) => {
            if (mirror.current) mirror.current.scrollTop = e.currentTarget.scrollTop;
          }}
        />
      </div>
      <span className="text-xs leading-snug text-muted">
        Type {"{{"} to add a variable, e.g. {"{{output}}"}. Each one gets a row in step 3.
      </span>
    </div>
  );
}
